import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Instagram, User, Bike, MapPin, Mountain, Coffee, Compass, Quote, Calendar } from "lucide-react";
import { useContent } from "../context/ContentContext";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatMonthYear(value) {
    if (!value) return null;
    const m = /^(\d{4})-(\d{2})$/.exec(value);
    if (!m) return value;
    const monthIdx = parseInt(m[2], 10) - 1;
    if (monthIdx < 0 || monthIdx > 11) return value;
    return `${MONTHS[monthIdx]} ${m[1]}`;
}
function formatTenure(from, to) {
    const f = formatMonthYear(from);
    if (!f) return null;
    return `${f} — ${formatMonthYear(to) || "Present"}`;
}

function StatRow({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3 py-2">
            <Icon className="w-3.5 h-3.5 text-meraki-gold mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-[0.25em] text-meraki-muted">{label}</div>
                <div className="text-xs text-meraki-text mt-0.5 break-words">{value}</div>
            </div>
        </div>
    );
}

function MemberCard({ m }) {
    const hasStats = m.ride || m.favorite_route || m.dream_ride || m.ride_fuel || m.ride_style;
    return (
        <div
            data-testid={`member-card-${m.id}`}
            className="gallery-card group flex flex-col bg-meraki-surface border border-meraki-gold/20 overflow-hidden hover:border-meraki-gold/60 transition-colors"
        >
            {/* Photo */}
            <div className="relative aspect-[4/5] overflow-hidden bg-black">
                {m.photo_base64 ? (
                    <img src={m.photo_base64} alt={m.name} className="w-full h-full object-cover gallery-img" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-meraki-gold/30">
                        <User className="w-20 h-20" />
                    </div>
                )}
                <div className="absolute top-3 left-3 flex flex-col items-start gap-2">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-meraki-bg bg-meraki-gold px-2 py-1">
                        {m.role === "cofounder" ? "Co-Founder" : "Ambassador"}
                    </div>
                    {m.chapter && (
                        <div className="text-[9px] uppercase tracking-[0.3em] text-meraki-gold bg-black/80 backdrop-blur-sm border border-meraki-gold/30 px-2 py-1">
                            {m.chapter}
                        </div>
                    )}
                </div>
            </div>

            {/* Identity block */}
            <div className="p-5 border-b border-meraki-gold/15">
                <div className="font-display uppercase text-meraki-text text-2xl leading-none tracking-wide">{m.name}</div>
                {m.title && (
                    <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mt-2">{m.title}</div>
                )}
                {(() => {
                    const tenure = formatTenure(m.period_from, m.period_to);
                    return tenure ? (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                            <Calendar className="w-3 h-3" />
                            <span>{tenure}</span>
                        </div>
                    ) : null;
                })()}
                {m.bio && (
                    <p className="text-xs text-meraki-text/75 leading-relaxed mt-3 whitespace-pre-line">{m.bio}</p>
                )}
                {m.instagram && (
                    <a
                        href={m.instagram.startsWith("http") ? m.instagram : `https://instagram.com/${m.instagram.replace("@", "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 mt-3 text-[10px] uppercase tracking-[0.25em] text-meraki-gold/80 hover:text-meraki-gold transition-colors"
                    >
                        <Instagram className="w-3 h-3" />
                        <span>{m.instagram.replace("https://instagram.com/", "").replace(/^@?/, "@")}</span>
                    </a>
                )}
            </div>

            {/* Quick stats */}
            {hasStats && (
                <div className="px-5 py-3 divide-y divide-meraki-gold/10 flex-1">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-meraki-gold pb-2">Rider Profile</div>
                    <div className="pt-1">
                        <StatRow icon={Bike} label="Ride" value={m.ride} />
                        <StatRow icon={MapPin} label="Favorite Route" value={m.favorite_route} />
                        <StatRow icon={Mountain} label="Dream Ride" value={m.dream_ride} />
                        <StatRow icon={Coffee} label="Ride Fuel" value={m.ride_fuel} />
                        <StatRow icon={Compass} label="Ride Style" value={m.ride_style} />
                    </div>
                </div>
            )}

            {/* Signature */}
            {m.signature && (
                <div className="px-5 py-4 bg-black/40 border-t border-meraki-gold/15 mt-auto">
                    <div className="flex items-start gap-3">
                        <Quote className="w-4 h-4 text-meraki-gold shrink-0 mt-0.5" />
                        <p className="text-xs italic text-meraki-text/85 leading-relaxed">{m.signature}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState({ label }) {
    return (
        <div className="col-span-full border border-dashed border-meraki-gold/20 p-12 text-center">
            <div className="text-meraki-muted text-sm">No {label} added yet. Add them from the Admin panel.</div>
        </div>
    );
}

export default function Leadership() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { content } = useContent();
    const l = content.leaders;

    useEffect(() => {
        api.get("/members").then((r) => setMembers(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const cofounders = members.filter((m) => m.role === "cofounder");
    const ambassadors = members.filter((m) => m.role === "ambassador");

    const chapterCounts = useMemo(() => {
        const map = {};
        members.forEach((m) => {
            if (m.chapter) map[m.chapter] = (map[m.chapter] || 0) + 1;
        });
        return Object.entries(map).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    }, [members]);

    return (
        <section id="leaders" data-testid="leaders-section" className="relative py-24 md:py-40 bg-meraki-bg">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono text-meraki-gold text-sm">04</span>
                    <div className="w-8 h-px bg-meraki-gold" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold">{l.eyebrow}</span>
                </div>
                <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-meraki-text mb-8">
                    {l.heading_line1}<br />{l.heading_line2}
                </h2>

                {chapterCounts.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-16" data-testid="chapter-chips">
                        {chapterCounts.map(([name, count]) => (
                            <span
                                key={name}
                                data-testid={`chapter-chip-${name}`}
                                className="inline-flex items-center gap-2 border border-meraki-gold/30 bg-meraki-surface px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-meraki-text"
                            >
                                <MapPin className="w-3 h-3 text-meraki-gold" />
                                {name}
                                <span className="text-meraki-gold font-mono text-xs">{count} {count === 1 ? "rider" : "riders"}</span>
                            </span>
                        ))}
                    </div>
                )}

                <div className="mb-20">
                    <div className="flex items-baseline justify-between mb-8 border-b border-meraki-gold/20 pb-4">
                        <h3 className="font-heading text-xl md:text-2xl uppercase tracking-[0.25em] text-meraki-gold">{l.cofounders_label}</h3>
                        <span className="text-xs uppercase tracking-[0.25em] text-meraki-muted">Governor Bodies</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {loading ? null : cofounders.length === 0 ? <EmptyState label="cofounders" /> : cofounders.map((m) => <MemberCard key={m.id} m={m} />)}
                    </div>
                </div>

                <div>
                    <div className="flex items-baseline justify-between mb-8 border-b border-meraki-gold/20 pb-4">
                        <h3 className="font-heading text-xl md:text-2xl uppercase tracking-[0.25em] text-meraki-gold">{l.ambassadors_label}</h3>
                        <span className="text-xs uppercase tracking-[0.25em] text-meraki-muted">{ambassadors.length} Active</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {loading ? null : ambassadors.length === 0 ? <EmptyState label="ambassadors" /> : ambassadors.map((m) => <MemberCard key={m.id} m={m} />)}
                    </div>
                </div>
            </div>
        </section>
    );
}
